import React from "react";
import { ShoppingBag, ArrowUpRight, Sparkles, Heart, ExternalLink } from "lucide-react";
import { PRINTIFY_URL, FEATURED_PRODUCTS, SHOP_CATEGORIES } from "@/lib/config";

const ShopPage = () => {
  return (
    <div className="py-24 max-w-6xl mx-auto px-6 text-left space-y-24 animate-fade-in" data-testid="shop-page">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto space-y-5">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"The AGG Shop"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
          {"Wear the Mission"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
          {"More than merchandise — every piece is a statement. When you wear AGG, you carry a message of confidence, leadership, and sisterhood with you."}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
          <a
            href={PRINTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-hero-shop-btn"
            className="inline-flex items-center gap-2 bg-[#E94F9A] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#D13D86] shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <ShoppingBag className="h-4 w-4" /> {"Shop With Purpose"}
          </a>
          <a
            href={PRINTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-hero-store-link"
            className="inline-flex items-center gap-2 border border-[#4B1E5A] text-[#4B1E5A] rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#FFF5F9] transition-all"
          >
            {"Visit Full Store"} <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Featured products */}
      <div className="space-y-10">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"Featured"}</span>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Shop Our Collection"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Checkout is handled securely through our Printify store, which manages payments, shipping, and fulfillment."}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURED_PRODUCTS.map((p) => (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`shop-product-${p.id}`}
              className="group flex flex-col bg-white border border-[#E5E5E5]/60 rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-2 hover:border-[#E94F9A]/30 transition-all duration-300"
            >
              <div className="aspect-square overflow-hidden bg-[#FFF5F9]/40">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 space-y-2 flex flex-col flex-grow">
                <span className="font-sans text-[11px] font-semibold uppercase tracking-widest text-[#C8A96A]">{p.category}</span>
                <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{p.name}</h3>
                <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed flex-grow">{p.description}</p>
                <div className="flex items-center justify-between pt-3">
                  <span className="font-serif text-xl font-bold text-[#E94F9A]">{p.price}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4B1E5A] group-hover:text-[#E94F9A] transition-colors">
                    {"Shop Now"} <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </a>
          ))}

          {/* Coming soon teaser */}
          <div className="flex flex-col items-center justify-center text-center bg-[#FFF5F9]/30 border border-dashed border-[#E94F9A]/30 rounded-3xl p-10 space-y-3" data-testid="shop-coming-soon">
            <Sparkles className="h-8 w-8 text-[#E94F9A]" />
            <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{"More Drops Coming Soon"}</h3>
            <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">{"Apparel, journals, tote bags, and limited-edition collections are on the way."}</p>
          </div>
        </div>
      </div>

      {/* Wear the Mission banner */}
      <div className="bg-[#4B1E5A] rounded-[3rem] p-10 md:p-16 text-center relative overflow-hidden" data-testid="shop-wear-mission">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,#E94F9A,transparent_55%)]" />
        <div className="relative max-w-3xl mx-auto space-y-6">
          <span className="inline-block h-px w-16 bg-[#C8A96A] mx-auto" />
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white">{"Wear the Mission"}</h2>
          <p className="font-sans text-base text-white/85 leading-relaxed">
            {"When you purchase AGG merchandise, you're doing more than buying a product — you are investing in girls, supporting leadership development, and helping create opportunities for the next generation of confident, compassionate, future-ready young women."}
          </p>
          <a
            href={PRINTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-wear-mission-btn"
            className="inline-flex items-center gap-2 bg-[#E94F9A] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#D13D86] shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <Heart className="h-4 w-4" /> {"Every Purchase Makes an Impact"}
          </a>
        </div>
      </div>

      {/* Future categories */}
      <div className="space-y-8">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"The Collection Is Growing"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"AGG is building toward a full lifestyle shop. Here's a preview of what's ahead."}</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2.5">
          {SHOP_CATEGORIES.map((c) => (
            <span key={c} className="font-sans text-xs text-[#4A4A4A] bg-white border border-[#E5E5E5]/60 rounded-full px-4 py-2 hover:border-[#E94F9A]/40 hover:text-[#E94F9A] transition-all">
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
